import { FormEvent, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useLanguage } from "../hooks/useLanguage"
import { LogoSymbol } from "./Logo"
import { ArrowRight, ArrowUpRight } from "./icons"

type Status = "idle" | "sending" | "success" | "error"

const labelClass = "flex min-w-0 flex-col gap-2 text-[14px] font-medium text-gms-text-2"

export default function Contact() {
  const { t, language } = useLanguage()
  const c = t.contact
  const f = c.form
  const info = t.contactInfo
  const navigate = useNavigate()

  const [typeIndex, setTypeIndex] = useState(0)
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [company, setCompany] = useState("")
  const [message, setMessage] = useState("")
  const [status, setStatus] = useState<Status>("idle")
  const timers = useRef<number[]>([])

  useEffect(() => () => timers.current.forEach((id) => window.clearTimeout(id)), [])

  const channels = [
    { label: c.channels.whatsapp, value: info.whatsappDisplay, href: info.whatsappUrl },
    { label: c.channels.email, value: info.email, href: `mailto:${info.email}` },
    { label: c.channels.telegram, value: info.telegramHandle, href: info.telegramUrl },
  ]

  // Envio: abre o e-mail do visitante com tudo preenchido (mesma integração de antes)
  // e segue para /contact-confirmed, que é a página acompanhada pelo gtag.
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (status === "sending") return
    setStatus("sending")

    const type = f.types[typeIndex]
    const subject = `${f.mailSubject}: ${type} · ${name}${company ? ` (${company})` : ""}`
    const body = [
      `${f.name}: ${name}`,
      `${f.email}: ${email}`,
      `${f.mailCompany}: ${company || "-"}`,
      `${f.mailType}: ${type}`,
      "",
      message,
    ].join("\n")

    timers.current.push(
      window.setTimeout(() => {
        try {
          window.location.href = `mailto:${info.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
          setStatus("success")
          timers.current.push(
            window.setTimeout(() => navigate(language === "en" ? "/en/contact-confirmed" : "/contact-confirmed"), 1600)
          )
        } catch {
          setStatus("error")
        }
      }, 300)
    )
  }

  return (
    <section id="contato" className="gms-wrap pt-section">
      <div className="relative flex flex-wrap items-stretch gap-x-16 gap-y-12 overflow-hidden rounded-[36px] bg-gms-ink p-[clamp(32px,5vw,72px)] text-white">
        <LogoSymbol
          size={520}
          stroke="#FFFFFF"
          pixel="#FFFFFF"
          className="pointer-events-none absolute -left-24 bottom-[-150px] opacity-[0.06]"
        />

        <div className="relative flex min-w-0 flex-[1_1_440px] flex-col">
          <h2 className="text-[clamp(36px,4.5vw,70px)] font-semibold leading-[0.94] tracking-[-0.055em]">
            {c.titleStart}
            <span className="text-gms-accent-on-dark">{c.titleHighlight}</span>
          </h2>
          <p className="mt-[22px] max-w-[460px] text-[17px] leading-[1.55] text-gms-dark-body">{c.lead}</p>
          <div className="mt-auto flex flex-col pt-10">
            {channels.map(({ label, value, href }, i) => (
              <a
                key={label}
                href={href}
                target={href.startsWith("http") ? "_blank" : undefined}
                rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                className={`gms-row flex items-center justify-between gap-4 border-t border-gms-dark-rule py-[18px] text-white no-underline hover:text-white ${
                  i === channels.length - 1 ? "border-b" : ""
                }`}
              >
                <span className="min-w-0">
                  <span className="block font-mono text-[12px] text-gms-dark-muted">{label}</span>
                  <span className="mt-1 block text-[20px] font-semibold [overflow-wrap:anywhere]">{value}</span>
                </span>
                <ArrowUpRight size={20} className="flex-none text-gms-accent-on-dark" />
              </a>
            ))}
          </div>
        </div>

        <form
          aria-label={f.label}
          onSubmit={handleSubmit}
          className="relative flex min-w-0 flex-[1.15_1_440px] flex-col gap-5 rounded-[24px] bg-white p-[clamp(24px,3vw,36px)] text-gms-ink"
        >
          <fieldset className="m-0 border-0 p-0">
            <legend className="p-0 text-[15px] font-semibold">{f.typeLegend}</legend>
            <div className="mt-3 flex flex-wrap gap-2">
              {f.types.map((type, i) => {
                const on = i === typeIndex
                return (
                  <button
                    key={type}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setTypeIndex(i)}
                    className={`inline-flex min-h-[44px] cursor-pointer items-center rounded-full border px-4 text-[15px] font-medium transition-colors duration-150 ${
                      on ? "border-gms-ink bg-gms-ink text-white" : "border-gms-line-2 bg-white text-gms-ink hover:border-gms-ink"
                    }`}
                  >
                    {type}
                  </button>
                )
              })}
            </div>
          </fieldset>

          <div className="flex flex-wrap gap-x-[14px] gap-y-5">
            <label className={`${labelClass} flex-[1_1_200px]`}>
              {f.name}
              <input
                type="text"
                name="nome"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={f.namePh}
                autoComplete="name"
                className="gms-input"
              />
            </label>
            <label className={`${labelClass} flex-[1_1_200px]`}>
              {f.email}
              <input
                type="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={f.emailPh}
                autoComplete="email"
                className="gms-input"
              />
            </label>
          </div>
          <label className={labelClass}>
            {f.company}
            <input
              type="text"
              name="empresa"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder={f.companyPh}
              autoComplete="organization"
              className="gms-input"
            />
          </label>
          <label className={labelClass}>
            {f.message}
            <textarea
              name="mensagem"
              rows={4}
              required
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={f.messagePh}
              className="gms-input resize-y py-[14px] leading-[1.5]"
            />
          </label>

          <button
            type="submit"
            disabled={status === "sending"}
            className="gms-btn inline-flex min-h-[58px] cursor-pointer items-center justify-center gap-[10px] rounded-full border-0 bg-gms-accent text-[17px] font-semibold text-white"
          >
            {status === "sending" ? f.sending : f.submit}
            {status !== "sending" && <ArrowRight />}
          </button>

          <div role="status" aria-live="polite" className="empty:hidden">
            {status === "success" && (
              <p className="rounded-[14px] bg-gms-success-bg px-4 py-3 text-[15px] font-medium text-gms-success">{f.success}</p>
            )}
            {status === "error" && (
              <p className="rounded-[14px] bg-[#FDECEC] px-4 py-3 text-[15px] font-medium text-[#B3261E]">{f.error}</p>
            )}
          </div>

          <p className="flex items-center justify-center gap-2 text-center font-mono text-[12.5px] text-gms-subtle">
            <span aria-hidden="true" className="gms-live h-[7px] w-[7px] rounded-full bg-gms-live" />
            {f.note}
          </p>
        </form>
      </div>
    </section>
  )
}
